import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { useSearchParams } from 'react-router';
import { rateScrapeMetadataGetOptions } from '@quoruum/frontend-common/oas/gen/admin/@tanstack/react-query.gen';
import { getApiErrorMessage } from '@quoruum/frontend-common/api/errorMessage';
import { Modal } from '../components/Modal';

type ScreenshotItem = {
  imageUrl?: string;
  htmlUrl?: string;
  key: string;
  url?: string;
  tags?: Record<string, unknown>;
};

function parseRateShopEvent(tags: Record<string, unknown> | undefined): {
  externalId: string | null;
  arrivalDate: string | null;
  departureDate: string | null;
} {
  const event = tags?.['event'];
  if (typeof event !== 'string') {
    return { externalId: null, arrivalDate: null, departureDate: null };
  }
  const parsed: Record<string, string> = {};
  for (const token of event.trim().split(/\s+/)) {
    const colonIdx = token.indexOf(':');
    if (colonIdx === -1) continue;
    parsed[token.slice(0, colonIdx)] = token.slice(colonIdx + 1);
  }
  return {
    externalId: parsed['externalId'] ?? null,
    arrivalDate: parsed['arrivalDate'] ?? null,
    departureDate: parsed['departureDate'] ?? null,
  };
}

function buildUrlFromTemplate(
  urlTemplate: string,
  localHotelId: string | undefined,
  dateFormat: string | undefined,
  arrivalDateStr: string,
  departureDateStr: string
): string | null {
  if (!urlTemplate.trim() || !dateFormat?.trim()) return null;
  try {
    const arrivalDate = new Date(`${arrivalDateStr}T00:00:00`);
    const departureDate = new Date(`${departureDateStr}T00:00:00`);
    if (Number.isNaN(arrivalDate.getTime()) || Number.isNaN(departureDate.getTime())) return null;
    const formattedArrival = format(arrivalDate, dateFormat);
    const formattedDeparture = format(departureDate, dateFormat);
    const localHotelIdValue =
      typeof localHotelId === 'string' && localHotelId.trim() ? localHotelId.trim() : '';
    return urlTemplate
      .split('${local_hotel_id}')
      .join(localHotelIdValue)
      .split('${arrival_date}')
      .join(formattedArrival)
      .split('${departure_date}')
      .join(formattedDeparture);
  } catch {
    return null;
  }
}

const isScreenshotItem = (item: unknown): item is ScreenshotItem => {
  if (typeof item !== 'object' || item === null) {
    return false;
  }

  const value = item as Record<string, unknown>;

  return (
    typeof value.key === 'string' &&
    (typeof value.imageUrl === 'string' || typeof value.imageUrl === 'undefined') &&
    (typeof value.url === 'string' || typeof value.url === 'undefined') &&
    (typeof value.tags === 'object' || typeof value.tags === 'undefined') &&
    (typeof value.htmlUrl === 'string' || typeof value.htmlUrl === 'undefined')
  );
};

const MAX_CAROUSEL_ITEMS = 6;
const MIN_MODAL_ZOOM = 1;
const MAX_MODAL_ZOOM = 4;

export function TraceViewer(): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const traceId = searchParams.get('traceId') ?? '';
  const [inputTraceId, setInputTraceId] = useState(traceId);
  const [currentScreenshotIndex, setCurrentScreenshotIndex] = useState(0);
  const [carouselStartIndex, setCarouselStartIndex] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [modalZoom, setModalZoom] = useState(MIN_MODAL_ZOOM);
  const [modalPan, setModalPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0 });

  const handleGo = (): void => {
    const nextParams = new URLSearchParams(searchParams);

    if (inputTraceId.trim()) {
      nextParams.set('traceId', inputTraceId);
    } else {
      nextParams.delete('traceId');
    }

    setSearchParams(nextParams);
  };

  useEffect(() => {
    setInputTraceId(traceId);
  }, [traceId]);

  const {
    data: screenshots = [],
    isLoading: isLoadingScreenshots,
    error: screenshotsError,
  } = useQuery({
    queryKey: ['trace-viewer-screenshots', traceId],
    enabled: Boolean(traceId.trim()),
    queryFn: async (): Promise<ScreenshotItem[]> => {
      const response = await fetch(`/api/traces/${traceId}/screenshots`);

      if (!response.ok) {
        throw new Error(`Failed to load screenshots (${response.status})`);
      }

      const data = (await response.json()) as unknown;

      if (!Array.isArray(data) || !data.every(isScreenshotItem)) {
        throw new Error('Unexpected screenshots response format');
      }

      return data;
    },
  });

  useEffect(() => {
    setCurrentScreenshotIndex(0);
    setCarouselStartIndex(0);
  }, [traceId, screenshots.length]);

  useEffect(() => {
    if (!isImageModalOpen) {
      setModalZoom(MIN_MODAL_ZOOM);
      setModalPan({ x: 0, y: 0 });
      setIsPanning(false);
    }
  }, [isImageModalOpen]);

  useEffect(() => {
    setModalZoom(MIN_MODAL_ZOOM);
    setModalPan({ x: 0, y: 0 });
    setIsPanning(false);
  }, [currentScreenshotIndex]);

  const screenshotsWithImages = useMemo(
    () => screenshots.filter((screenshot) => typeof screenshot.imageUrl === 'string'),
    [screenshots]
  );

  const additionalHtmlItems = useMemo(
    () =>
      screenshots.filter(
        (screenshot) =>
          typeof screenshot.imageUrl !== 'string' && typeof screenshot.htmlUrl === 'string'
      ),
    [screenshots]
  );

  const hasScreenshots = screenshotsWithImages.length > 0;
  const currentScreenshot = hasScreenshots ? screenshotsWithImages[currentScreenshotIndex] : null;
  const maxCarouselStartIndex = Math.max(0, screenshotsWithImages.length - MAX_CAROUSEL_ITEMS);
  const visibleScreenshots = screenshotsWithImages.slice(
    carouselStartIndex,
    carouselStartIndex + MAX_CAROUSEL_ITEMS
  );

  const parsedEvent = useMemo(
    () => parseRateShopEvent(currentScreenshot?.tags),
    [currentScreenshot]
  );

  const { data: rateScrapeMetadata } = useQuery({
    ...rateScrapeMetadataGetOptions({
      path: { externalId: parsedEvent.externalId ?? '' },
    }),
    enabled: Boolean(parsedEvent.externalId),
    staleTime: 60 * 1000,
  });

  const builtUrl = useMemo(() => {
    if (
      !rateScrapeMetadata?.urlTemplate ||
      !parsedEvent.arrivalDate ||
      !parsedEvent.departureDate
    ) {
      return null;
    }
    return buildUrlFromTemplate(
      rateScrapeMetadata.urlTemplate,
      rateScrapeMetadata.localHotelId,
      rateScrapeMetadata.dateFormat,
      parsedEvent.arrivalDate,
      parsedEvent.departureDate
    );
  }, [rateScrapeMetadata, parsedEvent]);

  const showPreviousCarouselImages = (): void => {
    if (!hasScreenshots) {
      return;
    }

    setCarouselStartIndex((index) => Math.max(0, index - 1));
  };

  const showNextCarouselImages = (): void => {
    if (!hasScreenshots) {
      return;
    }

    setCarouselStartIndex((index) => Math.min(maxCarouselStartIndex, index + 1));
  };

  const handleModalZoomChange = (nextZoom: number): void => {
    setModalZoom(nextZoom);
    if (nextZoom <= MIN_MODAL_ZOOM) {
      setModalPan({ x: 0, y: 0 });
      setIsPanning(false);
    }
  };

  const handlePanStart = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (modalZoom <= MIN_MODAL_ZOOM) {
      return;
    }

    event.preventDefault();
    setIsPanning(true);
    panStartRef.current = {
      x: event.clientX - modalPan.x,
      y: event.clientY - modalPan.y,
    };
  };

  const handlePanMove = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (!isPanning || modalZoom <= MIN_MODAL_ZOOM) {
      return;
    }

    setModalPan({
      x: event.clientX - panStartRef.current.x,
      y: event.clientY - panStartRef.current.y,
    });
  };

  const handlePanEnd = (): void => {
    setIsPanning(false);
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Trace Viewer</h1>
      <div className="mt-4 flex max-w-lg flex-col gap-2">
        <label htmlFor="trace-id" className="text-sm font-medium text-slate-700">
          Trace id:
        </label>
        <form
          className="flex items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            handleGo();
          }}
        >
          <input
            id="trace-id"
            type="text"
            value={inputTraceId}
            onChange={(event) => setInputTraceId(event.target.value)}
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
            placeholder="Enter trace id"
          />
          <button
            type="submit"
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
          >
            Go
          </button>
        </form>
      </div>

      <section className="mt-8 rounded-lg border border-slate-200 p-4">
        <h2 className="text-lg font-semibold text-slate-900">Sentry Logs</h2>
        <p className="mt-2 text-sm text-slate-600">Embedded Sentry Logs Coming Soon!</p>
        <div className="mt-3 flex gap-4">
          <a
            href={`https://quoruum.sentry.io/explore/logs/trace/${traceId}?tab=logs`}
            target="_blank"
            rel="noreferrer"
            className="inline-block text-sm font-medium text-sky-700 underline decoration-sky-300 underline-offset-2 hover:text-sky-800"
          >
            Sentry Trace
          </a>
          <a
            href={`https://quoruum.sentry.io/explore/logs/?live=enabled&logsFields=timestamp&logsFields=message&logsQuery=trace%3A${traceId}&logsSortBys=-timestamp&project=-1&statsPeriod=1h`}
            target="_blank"
            rel="noreferrer"
            className="inline-block text-sm font-medium text-sky-700 underline decoration-sky-300 underline-offset-2 hover:text-sky-800"
          >
            Live Log
          </a>
        </div>
      </section>

      <section className="mt-6 rounded-lg border border-slate-200 p-4">
        <h2 className="text-lg font-semibold text-slate-900">Screenshots</h2>

        {!traceId.trim() ? (
          <p className="mt-2 text-sm text-slate-600">Enter a trace id to load screenshots.</p>
        ) : null}

        {isLoadingScreenshots ? (
          <p className="mt-2 text-sm text-slate-600">Loading screenshots...</p>
        ) : null}

        {screenshotsError ? (
          <p className="mt-2 text-sm text-red-700">
            {getApiErrorMessage(screenshotsError, 'Unable to load screenshots')}
          </p>
        ) : null}

        {!isLoadingScreenshots && !screenshotsError && traceId.trim() && !hasScreenshots ? (
          <p className="mt-2 text-sm text-slate-600">No screenshots found.</p>
        ) : null}

        {additionalHtmlItems.length > 0 ? (
          <section className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3">
            <h3 className="text-sm font-semibold text-slate-900">Additional Htmls</h3>
            <p className="mt-1 text-xs text-slate-600">
              These were HTMLs that were captured but the corresponding image screenshot failed
            </p>
            <ul className="mt-2 space-y-1">
              {additionalHtmlItems.map((item) => (
                <li key={item.key}>
                  <a
                    href={item.htmlUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-sky-700 underline decoration-sky-300 underline-offset-2 hover:text-sky-800"
                  >
                    {item.htmlUrl}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {currentScreenshot ? (
          <div className="mt-4">
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
              <img
                src={currentScreenshot.imageUrl}
                alt={`Screenshot ${currentScreenshotIndex + 1}`}
                className="h-[360px] w-full cursor-zoom-in object-contain"
                onClick={() => setIsImageModalOpen(true)}
              />
            </div>
            {currentScreenshot.htmlUrl ? (
              <div className="mt-2 flex justify-center gap-3">
                <a
                  href={currentScreenshot.htmlUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-center text-sm text-sky-700 underline decoration-sky-300 underline-offset-2 hover:text-sky-800"
                >
                  View Page
                </a>
              </div>
            ) : null}
            <SyntaxHighlighter
              language="json"
              customStyle={{
                marginTop: '0.5rem',
                marginBottom: 0,
                padding: '0.5rem',
                borderRadius: '0.375rem',
                fontSize: '0.75rem',
                lineHeight: 1.4,
              }}
              wrapLongLines
            >
              {JSON.stringify(currentScreenshot.tags, null, 2)}
            </SyntaxHighlighter>
            {builtUrl ? (
              <a
                href={builtUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-2 block text-center text-sm text-sky-700 underline decoration-sky-300 underline-offset-2 hover:text-sky-800"
              >
                {builtUrl}
              </a>
            ) : null}
            <div className="mt-3 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={showPreviousCarouselImages}
                disabled={carouselStartIndex === 0}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <div className="flex flex-1 items-center justify-center gap-2">
                {visibleScreenshots.map((screenshot, offset) => {
                  const absoluteIndex = carouselStartIndex + offset;
                  const isSelected = absoluteIndex === currentScreenshotIndex;

                  return (
                    <button
                      key={screenshot.key}
                      type="button"
                      onClick={() => setCurrentScreenshotIndex(absoluteIndex)}
                      className={`overflow-hidden rounded-md border transition ${
                        isSelected
                          ? 'border-sky-500 ring-2 ring-sky-500/30'
                          : 'border-slate-300 hover:border-slate-400'
                      }`}
                      aria-label={`View screenshot ${absoluteIndex + 1}`}
                    >
                      <img
                        src={screenshot.imageUrl}
                        alt={`Screenshot ${absoluteIndex + 1} thumbnail`}
                        className="h-12 w-16 object-cover"
                      />
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={showNextCarouselImages}
                disabled={carouselStartIndex >= maxCarouselStartIndex}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="mt-2 text-center text-sm text-slate-600">
              {currentScreenshotIndex + 1} / {screenshotsWithImages.length}
            </div>
          </div>
        ) : null}
      </section>

      {currentScreenshot ? (
        <Modal
          isOpen={isImageModalOpen}
          onClose={() => setIsImageModalOpen(false)}
          ariaLabel="Expanded screenshot preview"
          panelClassName="relative w-full max-w-7xl rounded-lg border border-slate-200 bg-white p-2 shadow-xl"
        >
          <button
            type="button"
            onClick={() => setIsImageModalOpen(false)}
            className="absolute right-4 top-4 z-10 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-100"
          >
            Close
          </button>
          <div className="mb-2 mt-10 flex items-center gap-3 px-2">
            <label htmlFor="modal-zoom" className="text-sm font-medium text-slate-700">
              Zoom
            </label>
            <input
              id="modal-zoom"
              type="range"
              min={MIN_MODAL_ZOOM}
              max={MAX_MODAL_ZOOM}
              step={0.1}
              value={modalZoom}
              onChange={(event) => handleModalZoomChange(Number(event.target.value))}
              className="h-2 w-56 cursor-pointer accent-sky-600"
            />
            <span className="text-sm text-slate-600">{Math.round(modalZoom * 100)}%</span>
          </div>
          <div
            className={`max-h-[78vh] overflow-hidden rounded-md bg-slate-50 ${
              modalZoom > MIN_MODAL_ZOOM ? (isPanning ? 'cursor-grabbing' : 'cursor-grab') : ''
            }`}
            onMouseDown={handlePanStart}
            onMouseMove={handlePanMove}
            onMouseUp={handlePanEnd}
            onMouseLeave={handlePanEnd}
          >
            <img
              src={currentScreenshot.imageUrl}
              alt={`Screenshot ${currentScreenshotIndex + 1} enlarged`}
              className="max-h-[78vh] w-full rounded-md object-contain select-none"
              style={{
                transform: `translate(${modalPan.x}px, ${modalPan.y}px) scale(${modalZoom})`,
                transformOrigin: 'center center',
                transition: isPanning ? 'none' : 'transform 100ms ease-out',
              }}
              draggable={false}
            />
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
